import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useUser } from "./UserContext";

// Create the context
const GoogleApiContext = createContext();

// Provider component
export const GoogleApiProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const { userData } = useUser();

  useEffect(() => {
    if (userData && userData.googleRefreshToken) {
      // console.log(userData);
      setRefreshToken(userData.googleRefreshToken);
      // console.log("Refresh Token set:", userData.googleRefreshToken);
    }
  }, [userData]);

  const refreshAccessToken = async () => {
    const tokenToUse =
      refreshToken || (userData && userData.googleRefreshToken);
    if (!tokenToUse) {
      console.error("No refresh token available from state or userData.");
      return;
    }
    try {
      const response = await axios.post(
        "https://oauth2.googleapis.com/token",
        null,
        {
          params: {
            client_id: process.env.REACT_APP_DRIVE_CLIENT_ID,
            client_secret: process.env.REACT_APP_DRIVE_CLIENT_SECRET,
            refresh_token: tokenToUse,
            grant_type: "refresh_token",
          },
        }
      );
      const newAccessToken = response.data.access_token;
      // console.log("New Access Token:", newAccessToken);
      setAccessToken(newAccessToken);
      return newAccessToken;
    } catch (err) {
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (
          (errorData.error && errorData.error === "invalid_grant") ||
          (errorData.ErrorCode === "invalid_request" &&
            errorData.Error === "Invalid Refresh Token") ||
          errorData.Error === "Refresh Token expired" ||
          errorData.Error === "Invalid Scope"
        ) {
          console.error(
            "Refresh token expired or revoked:",
            errorData.error_description || errorData.Error
          );
        }
      }
      console.error("Error refreshing access token:", err);
      throw err;
    }
  };

  const driveApiCall = async (apiCall, attempt = 1) => {
    const response = await apiCall();

    // If we got a 401, refresh and retry (up to 3 attempts)
    if (response.status === 401 && attempt < 3) {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        throw new Error("Could not refresh token");
      }
      // retry the same API call
      return driveApiCall(apiCall, attempt + 1);
    }

    return response;
  };

  const fetchPdfFile = async (googleFileId) => {
    // console.log("Fetching file:", googleFileId);
    const url = `https://www.googleapis.com/drive/v3/files/${googleFileId}?alt=media`;

    let tokenToUse = accessToken;

    // console.log(tokenToUse);
    if (!tokenToUse) {
      tokenToUse = await refreshAccessToken();
      if (!tokenToUse) {
        throw new Error("Unable to refresh access token");
      }
    }

    try {
      const response = await driveApiCall(() => {
        return fetch(url, {
          headers: {
            Authorization: `Bearer ${tokenToUse}`,
          },
        });
      });

      if (!response.ok) {
        throw new Error(`Error downloading file: ${response.statusText}`);
      }

      const pdfBytes = await response.arrayBuffer();
      return pdfBytes;
    } catch (error) {
      console.error("Error Fetching Pdf: ", error);
      throw error;
    }
  };

  return (
    <GoogleApiContext.Provider
      value={{
        accessToken,
        refreshToken,
        setRefreshToken,
        refreshAccessToken,
        driveApiCall,
        fetchPdfFile,
      }}
    >
      {children}
    </GoogleApiContext.Provider>
  );
};

export const useGoogleApi = () => {
  const context = useContext(GoogleApiContext);
  if (!context) {
    throw new Error("useGoogleApi must be used within a GoogleApiProvider");
  }
  return context;
};
